import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { 
  Plus, 
  Edit, 
  Trash2, 
  Heart, 
  Sparkles, 
  Waves, 
  Flame,
  Hand,
  Footprints,
  Save,
  X,
  Loader,
  Package,
  RotateCcw,
  Search,
  ChevronDown,
  ChevronUp,
  Percent
} from 'lucide-react'
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, getDoc, setDoc, query, orderBy, limit, startAfter } from 'firebase/firestore'
import { db, COLLECTIONS } from '../firebase/config'
import PageHelpButton from '../components/PageHelpButton'
import toast from 'react-hot-toast'

const SETTINGS_BOOKING = 'booking'

const Services = () => {
  const PAGE_SIZE = 50
  const [services, setServices] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [lastVisibleServices, setLastVisibleServices] = useState(null)
  const [lastVisibleCombos, setLastVisibleCombos] = useState(null)
  const [hasMore, setHasMore] = useState(false)

  const [showAddModal, setShowAddModal] = useState(false)
  const [editingService, setEditingService] = useState(null)
  const [formData, setFormData] = useState({
    name: '',
    category: 'Massage',
    description: '',
    spaPrice: '',
    mobilePrice: '',
    discount: '',
    duration: '',
    isActive: true,
    features: [],
    serviceNames: [] // For Combo Packages: included services (e.g. "Swedish Massage", "Hydrating Facial")
  })
  const [newFeature, setNewFeature] = useState('')
  const [newServiceName, setNewServiceName] = useState('') // For Combo: add included service name

  const categories = [
    { value: 'Massage', label: 'Massage', icon: Heart, color: 'text-purple-600' },
    { value: 'Facials', label: 'Facials', icon: Sparkles, color: 'text-pink-600' },
    { value: 'Wax', label: 'Wax', icon: Flame, color: 'text-amber-600' },
    { value: 'Body Scrubbing', label: 'Body Scrubbing', icon: Waves, color: 'text-amber-600' },
    { value: 'Manicures', label: 'Manicures', icon: Hand, color: 'text-rose-600' },
    { value: 'Pedicures', label: 'Pedicures', icon: Footprints, color: 'text-pink-600' },
    { value: 'Combo Packages', label: 'Combo Packages', icon: Package, color: 'text-purple-600' }
  ]

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    // For price fields (spaPrice, mobilePrice), allow any input (will validate on blur)
    if (name === 'spaPrice' || name === 'mobilePrice') {
      // Allow empty string, numbers, and decimal point
      if (value === '' || /^\d*\.?\d*$/.test(value)) {
        setFormData(prev => ({
          ...prev,
          [name]: value
        }))
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }))
    }
  }

  const handleAddFeature = () => {
    if (newFeature.trim()) {
      setFormData(prev => ({
        ...prev,
        features: [...prev.features, newFeature.trim()]
      }))
      setNewFeature('')
    }
  }

  const handleRemoveFeature = (index) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }))
  }

  const handleAddServiceName = () => {
    if (newServiceName.trim()) {
      setFormData(prev => ({
        ...prev,
        serviceNames: [...(prev.serviceNames || []), newServiceName.trim()]
      }))
      setNewServiceName('')
    }
  }

  const handleRemoveServiceName = (index) => {
    setFormData(prev => ({
      ...prev,
      serviceNames: (prev.serviceNames || []).filter((_, i) => i !== index)
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSubmitting(true)

    try {
      const serviceData = {
        name: formData.name,
        category: formData.category,
        description: formData.description,
        spaPrice: Math.round(Number(formData.spaPrice) * 100) / 100,
        mobilePrice: Math.round(Number(formData.mobilePrice) * 100) / 100,
        discount: parseFloat(formData.discount) || 0,
        duration: parseInt(formData.duration),
        isActive: formData.isActive,
        features: formData.features || [],
        createdAt: editingService?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }
      if (formData.category === 'Combo Packages') {
        serviceData.serviceNames = formData.serviceNames || []
        serviceData.isAutoGenerated = true // So main site shows "Included services" from serviceNames
      }

      // Determine which collection to use based on category
      const targetCollection = formData.category === 'Combo Packages' 
        ? COLLECTIONS.COMBO_PACKAGES 
        : COLLECTIONS.SERVICES

      // Check if editing and need to move between collections
      if (editingService) {
        const oldCollection = editingService.category === 'Combo Packages' 
          ? COLLECTIONS.COMBO_PACKAGES 
          : COLLECTIONS.SERVICES
        
        // If collection changed, delete from old and add to new
        if (oldCollection !== targetCollection) {
          await deleteDoc(doc(db, oldCollection, editingService.id))
          const docRef = await addDoc(collection(db, targetCollection), serviceData)
          toast.success('Service updated successfully!')
          
          // Update local state
          setServices(prev => prev
            .filter(service => service.id !== editingService.id)
            .concat([{ ...serviceData, id: docRef.id }])
          )
        } else {
          // Same collection, just update
          await updateDoc(doc(db, targetCollection, editingService.id), serviceData)
          toast.success('Service updated successfully!')
          
          // Update local state
          setServices(prev => prev.map(service => 
            service.id === editingService.id 
              ? { ...serviceData, id: editingService.id }
              : service
          ))
        }
      } else {
        // Add new service to appropriate collection
        const docRef = await addDoc(collection(db, targetCollection), serviceData)
        toast.success('Service added successfully!')
        
        // Update local state
        setServices(prev => [...prev, { ...serviceData, id: docRef.id }])
      }

      // Reset form and close modal
      setShowAddModal(false)
      setEditingService(null)
      setFormData({
        name: '',
        category: 'Massage',
        description: '',
        spaPrice: '',
        mobilePrice: '',
        discount: '',
        duration: '',
        isActive: true,
        features: [],
        serviceNames: []
      })
    } catch (error) {
      console.error('Error saving service:', error)
      toast.error('Failed to save service. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const handleEdit = (service) => {
    setEditingService(service)
    const spaPrice = service.spaPrice ?? service.price ?? ''
    const mobilePrice = service.mobilePrice ?? service.price ?? ''
    setFormData({
      ...service,
      spaPrice: spaPrice.toString(),
      mobilePrice: mobilePrice.toString(),
      discount: (service.discount ?? 0).toString(),
      duration: (service.duration ?? '').toString(),
      serviceNames: service.serviceNames || []
    })
    setShowAddModal(true)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // Scroll to top when modal opens
  useEffect(() => {
    if (showAddModal) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [showAddModal])

  const handleDelete = async (service) => {
    if (!window.confirm('Are you sure you want to delete this service?')) {
      return
    }

    try {
      // Determine which collection based on category
      const targetCollection = service.category === 'Combo Packages' 
        ? COLLECTIONS.COMBO_PACKAGES 
        : COLLECTIONS.SERVICES
      
      await deleteDoc(doc(db, targetCollection, service.id))
      toast.success('Service deleted successfully!')
      
      // Update local state
      setServices(prev => prev.filter(s => s.id !== service.id))
    } catch (error) {
      console.error('Error deleting service:', error)
      toast.error('Failed to delete service. Please try again.')
    }
  }

  const toggleServiceStatus = async (id) => {
    const service = services.find(s => s.id === id)
    if (!service) return

    try {
      const newStatus = !service.isActive
      // Determine which collection based on category
      const targetCollection = service.category === 'Combo Packages' 
        ? COLLECTIONS.COMBO_PACKAGES 
        : COLLECTIONS.SERVICES
      
      await updateDoc(doc(db, targetCollection, id), {
        isActive: newStatus,
        updatedAt: new Date().toISOString()
      })
      
      // Update local state
      setServices(prev => prev.map(s => 
        s.id === id ? { ...s, isActive: newStatus } : s
      ))
      toast.success(`Service ${newStatus ? 'activated' : 'deactivated'} successfully!`)
    } catch (error) {
      console.error('Error toggling service status:', error)
      toast.error('Failed to update service status.')
    }
  }

  const getCategoryIcon = (category) => {
    const categoryData = categories.find(cat => cat.value === category)
    return categoryData ? categoryData.icon : Heart
  }

  const getCategoryColor = (category) => {
    const categoryData = categories.find(cat => cat.value === category)
    return categoryData ? categoryData.color : 'text-gray-600'
  }

  const [filterCategory, setFilterCategory] = useState('All')
  const [searchQuery, setSearchQuery] = useState('')
  const [submitting, setSubmitting] = useState(false)

  // Promo / overall discount (stored in settings/booking; when > 0 overrides individual)
  const [promoDiscount, setPromoDiscount] = useState(0)
  const [promoDiscountReason, setPromoDiscountReason] = useState('')
  const [promoBlockOpen, setPromoBlockOpen] = useState(false)
  const [savingPromo, setSavingPromo] = useState(false)
  const [bulkDiscountValue, setBulkDiscountValue] = useState('')
  const [bulkDiscountApplying, setBulkDiscountApplying] = useState(false)

  const fetchServices = async (cursorServices = null, cursorCombos = null, append = false) => {
    try {
      if (append) setLoadingMore(true)
      else setLoading(true)
      const servicesRef = collection(db, COLLECTIONS.SERVICES)
      const combosRef = collection(db, COLLECTIONS.COMBO_PACKAGES)
      const servicesQ = cursorServices
        ? query(servicesRef, orderBy('name', 'asc'), limit(PAGE_SIZE), startAfter(cursorServices))
        : query(servicesRef, orderBy('name', 'asc'), limit(PAGE_SIZE))
      const combosQ = cursorCombos
        ? query(combosRef, orderBy('name', 'asc'), limit(PAGE_SIZE), startAfter(cursorCombos))
        : query(combosRef, orderBy('name', 'asc'), limit(PAGE_SIZE))
      const [servicesSnapshot, combosSnapshot] = await Promise.all([
        getDocs(servicesQ),
        getDocs(combosQ)
      ])
      const servicesData = servicesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      const combosData = combosSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      const lastS = servicesSnapshot.docs[servicesSnapshot.docs.length - 1] || null
      const lastC = combosSnapshot.docs[combosSnapshot.docs.length - 1] || null
      setLastVisibleServices(lastS)
      setLastVisibleCombos(lastC)
      setHasMore(servicesSnapshot.docs.length === PAGE_SIZE || combosSnapshot.docs.length === PAGE_SIZE)
      const combined = [...servicesData, ...combosData]
      if (append) setServices(prev => [...prev, ...combined])
      else setServices(combined)
    } catch (error) {
      console.error('Error fetching services:', error)
      toast.error('Failed to load services. Please try again.')
      setHasMore(false)
    } finally {
      if (append) setLoadingMore(false)
      else setLoading(false)
    }
  }

  const loadMoreServices = () => {
    if (loadingMore) return
    if (!lastVisibleServices && !lastVisibleCombos) return
    fetchServices(lastVisibleServices, lastVisibleCombos, true)
  }

  useEffect(() => {
    fetchServices()
  }, [])

  // Load promo discount from Firestore (settings/booking)
  useEffect(() => {
    const load = async () => {
      try {
        const ref = doc(db, 'settings', SETTINGS_BOOKING)
        const snap = await getDoc(ref)
        if (snap.exists()) {
          const data = snap.data()
          const v = data.promoDiscount
          setPromoDiscount(typeof v === 'number' && v >= 0 ? v : 0)
          setPromoDiscountReason(typeof data.promoDiscountReason === 'string' ? data.promoDiscountReason : '')
        }
      } catch (e) {
        console.error('Load promo discount:', e)
      }
    }
    load()
  }, [])

  const savePromoDiscount = async (value, reason) => {
    const num = Math.max(0, Math.min(100, Number(value) || 0))
    const reasonStr = typeof reason === 'string' ? reason.trim() : ''
    setSavingPromo(true)
    try {
      const ref = doc(db, 'settings', SETTINGS_BOOKING)
      await setDoc(ref, {
        promoDiscount: num,
        promoDiscountReason: reasonStr,
        updatedAt: new Date().toISOString()
      }, { merge: true })
      setPromoDiscount(num)
      setPromoDiscountReason(reasonStr)
      toast.success(num > 0 ? `Promo ${num}% saved${reasonStr ? ` (${reasonStr})` : ''}` : 'Promo discount cleared')
    } catch (e) {
      console.error('Save promo discount:', e)
      toast.error('Failed to save promo discount')
    } finally {
      setSavingPromo(false)
    }
  }

  const bulkSetAllDiscounts = async (percent) => {
    const value = Math.max(0, Math.min(100, Number(percent)))
    setBulkDiscountApplying(true)
    try {
      let updated = 0
      for (const service of services) {
        const targetCollection = service.category === 'Combo Packages' ? COLLECTIONS.COMBO_PACKAGES : COLLECTIONS.SERVICES
        await updateDoc(doc(db, targetCollection, service.id), {
          discount: value,
          updatedAt: new Date().toISOString()
        })
        updated++
      }
      setServices(prev => prev.map(s => ({ ...s, discount: value })))
      toast.success(`Updated ${updated} service(s) to ${value}% discount`)
      setBulkDiscountValue('')
    } catch (e) {
      console.error('Bulk discount update:', e)
      toast.error('Failed to update some services')
    } finally {
      setBulkDiscountApplying(false)
    }
  }

  const filteredServices = services.filter(service => {
    const matchesCategory = filterCategory === 'All' || service.category === filterCategory
    const q = (searchQuery || '').trim().toLowerCase()
    if (!q) return matchesCategory
    const matchName = (service.name || '').toLowerCase().includes(q)
    const matchCategory = (service.category || '').toLowerCase().includes(q)
    const matchDesc = (service.description || '').toLowerCase().includes(q)
    const matchFeatures = (service.features || []).some(f => String(f).toLowerCase().includes(q))
    return matchesCategory && (matchName || matchCategory || matchDesc || matchFeatures)
  })

  return (
    <div className="space-y-6 text-white">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-white">Services Management</h1>
          <p className="text-gray-300 mt-1">Manage your spa services, pricing, and availability</p>
        </div>
        <div className="flex items-center space-x-3 mt-4 sm:mt-0 flex-wrap gap-2">
          <button
            onClick={async () => {
              await fetchServices()
              toast.success('Services refreshed')
            }}
            disabled={loading}
            className="flex items-center space-x-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Refresh services"
          >
            <RotateCcw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
          <button
            onClick={() => {
              setShowAddModal(true)
              window.scrollTo({ top: 0, behavior: 'smooth' })
            }}
            className="btn-primary flex items-center space-x-2"
          >
            <Plus className="w-5 h-5" />
            <span>Add New Service</span>
          </button>
        </div>
      </div>

      <PageHelpButton
        videoId={2}
        title="Services management tutorial"
        youtubeUrl="https://www.youtube.com/embed/rG6SWlcZYOE"
      />

      {/* Search bar */}
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name, category, description, features..."
            className="w-full pl-9 pr-4 py-2.5 rounded-lg bg-gray-700 border border-gray-600 text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500"
          />
        </div>
        <div className="w-full sm:w-48">
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="w-full input-field bg-gray-700 border-gray-600 text-white"
          >
            <option value="All">All Categories</option>
            {categories.map((category) => (
              <option key={category.value} value={category.value}>
                {category.label}
              </option>
            ))}
          </select>
        </div>
        {searchQuery.trim() && (
          <span className="text-sm text-gray-400 whitespace-nowrap">{filteredServices.length} result(s)</span>
        )}
      </div>

      {/* Promo & discount controls (dropdown) */}
      <div className="bg-gray-800/80 rounded-lg border border-gray-700 overflow-hidden">
        <button
          type="button"
          onClick={() => setPromoBlockOpen(prev => !prev)}
          className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-700/50 transition-colors"
        >
          <div className="flex items-center space-x-3">
            <Percent className="w-5 h-5 text-purple-400" />
            <span className="font-medium text-white">Promo & discount controls</span>
            {promoDiscount > 0 && (
              <span className="text-xs text-purple-300">(Promo {promoDiscount}% active)</span>
            )}
          </div>
          {promoBlockOpen ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
        </button>
        {promoBlockOpen && (
          <div className="border-t border-gray-700 p-4 space-y-4">
            <p className="text-sm text-gray-400">
              When promo discount is set above 0, the main site uses it for all services. The reason appears in the discount ribbon on the main site (e.g. &quot;New Year&quot;).
            </p>
            <div className="flex flex-wrap items-end gap-4">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Promo discount (%) – overrides individual when &gt; 0</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    value={promoDiscount || ''}
                    onChange={(e) => setPromoDiscount(e.target.value === '' ? '' : Math.max(0, Math.min(100, Number(e.target.value))))}
                    className="w-24 input-field bg-gray-700 border-gray-600 text-white"
                    placeholder="0"
                  />
                  <input
                    type="text"
                    value={promoDiscountReason}
                    onChange={(e) => setPromoDiscountReason(e.target.value)}
                    className="w-40 input-field bg-gray-700 border-gray-600 text-white"
                    placeholder="e.g. New Year"
                    title="Reason for promo (shows in discount ribbon)"
                  />
                  <button
                    type="button"
                    onClick={() => savePromoDiscount(promoDiscount, promoDiscountReason)}
                    disabled={savingPromo}
                    className="px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg text-sm font-medium disabled:opacity-50"
                  >
                    {savingPromo ? <Loader className="w-4 h-4 animate-spin inline" /> : 'Save'}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">Reason is shown on the main site discount ribbon (e.g. &quot;5% OFF · New Year&quot;).</p>
              </div>
            </div>
            <div className="pt-2 border-t border-gray-700">
              <p className="text-xs text-gray-400 mb-3">Bulk update individual service discounts:</p>
              <div className="flex flex-wrap items-end gap-3">
                <button
                  type="button"
                  onClick={() => bulkSetAllDiscounts(0)}
                  disabled={bulkDiscountApplying || services.length === 0}
                  className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg text-sm font-medium disabled:opacity-50"
                >
                  {bulkDiscountApplying ? 'Updating...' : 'Set all to 0%'}
                </button>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    value={bulkDiscountValue}
                    onChange={(e) => setBulkDiscountValue(e.target.value)}
                    className="w-20 input-field bg-gray-700 border-gray-600 text-white py-2"
                    placeholder="%"
                  />
                  <button
                    type="button"
                    onClick={() => bulkSetAllDiscounts(bulkDiscountValue)}
                    disabled={bulkDiscountApplying || services.length === 0 || bulkDiscountValue === ''}
                    className="px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg text-sm font-medium disabled:opacity-50"
                  >
                    Set all to this %
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Services Table */}
      <div className="bg-gray-800 bg-opacity-50 backdrop-blur-sm rounded-lg border border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider w-20 min-w-[4rem]">EDIT</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">NAME</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">CATEGORY</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">PRICE</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">DURATION</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">FEATURES & BENEFITS</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">STATUS</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">DISCOUNT (%)</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">DISPLAYED PRICE</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider w-20 min-w-[4rem]">DELETE</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-600">
              {loading ? (
                <tr>
                  <td colSpan={10} className="px-6 py-8 text-center">
                    <div className="flex items-center justify-center space-x-2 text-gray-300">
                      <Loader className="w-5 h-5 animate-spin" />
                      <span>Loading services...</span>
                    </div>
                  </td>
                </tr>
              ) : filteredServices.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-6 py-8 text-center text-gray-400">
                    No services found. Try a different search or category, or click "Add New Service".
                  </td>
                </tr>
              ) : (
                filteredServices.map((service) => {
                const IconComponent = getCategoryIcon(service.category)
                const effectiveDiscount = promoDiscount > 0 ? promoDiscount : (service.discount ?? 0)
                const displayPriceSpa = ((service.spaPrice ?? service.price ?? 0) * 1.02) * (1 - effectiveDiscount / 100)
                const displayPriceMobile = ((service.mobilePrice ?? service.price ?? 0) * 1.02) * (1 - effectiveDiscount / 100)
                return (
                  <tr key={service.id} className="hover:bg-gray-700 bg-opacity-50">
                    <td className="px-2 py-4 whitespace-nowrap align-middle">
                      <button
                        type="button"
                        onClick={() => handleEdit(service)}
                        className="min-h-[44px] min-w-[44px] flex items-center justify-center p-3 rounded-lg text-blue-400 hover:text-blue-300 hover:bg-gray-700/80 transition-colors touch-manipulation"
                        title="Edit service"
                        aria-label="Edit service"
                      >
                        <Edit className="w-5 h-5" />
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-white">{service.name}</div>
                        <div className="text-sm text-gray-300 line-clamp-1">{service.description}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <IconComponent className={`w-4 h-4 ${getCategoryColor(service.category)}`} />
                        <span className="text-sm text-gray-300">{service.category}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                      <div className="space-y-1">
                        <div className="font-semibold">Spa: GHS {service.spaPrice ?? service.price ?? 'N/A'}</div>
                        <div className="text-gray-400 text-xs">Mobile: GHS {service.mobilePrice ?? service.price ?? 'N/A'}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      {service.duration}m
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-wrap gap-1 max-w-xs">
                        {service.features && service.features.length > 0 ? (
                          <>
                            {service.features.slice(0,3).map((feature, idx) => (
                              <span key={idx} className="text-xs bg-gray-700 text-gray-300 px-2 py-1 rounded">
                                {feature}
                              </span>
                            ))}
                            {service.features.length > 3 && (
                              <span className="text-xs bg-gray-700 text-gray-300 px-2 py-1 rounded">
                                +{service.features.length - 3} more
                              </span>
                            )}
                          </>
                        ) : (
                          <span className="text-xs text-gray-400">No features</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        service.isActive 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {service.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-white font-semibold">
                      {promoDiscount > 0 ? (
                        <span className="text-purple-300" title="Promo override">{effectiveDiscount}%</span>
                      ) : (
                        <span>{service.discount ?? 0}%</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                      <div className="space-y-1">
                        <div className="font-semibold text-green-300">Spa: GHS {displayPriceSpa.toFixed(2)}</div>
                        <div className="font-semibold text-green-300">Mobile: GHS {displayPriceMobile.toFixed(2)}</div>
                      </div>
                    </td>
                    <td className="px-2 py-4 whitespace-nowrap align-middle">
                      <button
                        type="button"
                        onClick={() => handleDelete(service)}
                        className="min-h-[44px] min-w-[44px] flex items-center justify-center p-3 rounded-lg text-red-400 hover:text-red-300 hover:bg-gray-700/80 transition-colors touch-manipulation"
                        title="Delete service"
                        aria-label="Delete service"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </td>
                  </tr>
                )
              })
              )}
            </tbody>
          </table>
        </div>
        {!loading && services.length > 0 && (
          <div className="border-t border-gray-700 px-6 py-3 flex items-center justify-between bg-gray-800/50">
            <p className="text-xs text-gray-400">Load more only when needed to reduce reads.</p>
            <button
              type="button"
              onClick={loadMoreServices}
              disabled={!hasMore || loadingMore}
              className="inline-flex items-center space-x-2 px-4 py-2 rounded-lg text-xs font-medium bg-gray-700 hover:bg-gray-600 text-white border border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loadingMore ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  <span>Loading...</span>
                </>
              ) : hasMore ? (
                <span>Load more services</span>
              ) : (
                <span>No more</span>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Add/Edit Service Modal - portaled so header is below top bar */}
      {showAddModal && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-70 z-[100] flex items-start justify-center p-4 pt-20 overflow-y-auto">
          <div className="bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full my-auto max-h-[calc(100vh-5rem)] flex flex-col overflow-hidden border border-gray-700">
            <div className="flex-shrink-0 bg-gray-800 border-b border-gray-700 px-6 py-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-white">
                  {editingService ? 'Edit Service' : 'Add New Service'}
                </h2>
                <button
                  onClick={() => {
                    setShowAddModal(false)
                    setEditingService(null)
                    setFormData({
                      name: '',
                      category: 'Massage',
                      description: '',
                      spaPrice: '',
                      mobilePrice: '',
                      discount: '',
                      duration: '',
                      isActive: true,
                      features: [],
                      serviceNames: []
                    })
                  }}
                  className="text-gray-400 hover:text-white"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            <div className="p-6 overflow-y-auto flex-1 min-h-0">

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Service Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="input-field bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                      placeholder="Enter service name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Category *
                    </label>
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      className="input-field bg-gray-700 border-gray-600 text-white"
                    >
                      {categories.map((category) => (
                        <option key={category.value} value={category.value}>
                          {category.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Description *
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    required
                    rows={4}
                    className="input-field bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                    placeholder="Describe the service in detail"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Spa Price (GHS) *
                    </label>
                    <input
                      type="text"
                      name="spaPrice"
                      value={formData.spaPrice}
                      onChange={handleInputChange}
                      required
                      pattern="^\d+(\.\d{1,2})?$"
                      className="input-field bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                      placeholder="0.00"
                      onBlur={(e) => {
                        // Validate and format on blur
                        const value = e.target.value.trim()
                        if (value && !isNaN(value) && value !== '') {
                          const num = parseFloat(value)
                          if (!isNaN(num) && num >= 0) {
                            setFormData(prev => ({
                              ...prev,
                              spaPrice: num.toString()
                            }))
                          }
                        }
                      }}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Mobile Spa Price (GHS) *
                    </label>
                    <input
                      type="text"
                      name="mobilePrice"
                      value={formData.mobilePrice}
                      onChange={handleInputChange}
                      required
                      pattern="^\d+(\.\d{1,2})?$"
                      className="input-field bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                      placeholder="0.00"
                      onBlur={(e) => {
                        // Validate and format on blur
                        const value = e.target.value.trim()
                        if (value && !isNaN(value) && value !== '') {
                          const num = parseFloat(value)
                          if (!isNaN(num) && num >= 0) {
                            setFormData(prev => ({
                              ...prev,
                              mobilePrice: num.toString()
                            }))
                          }
                        }
                      }}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Discount (%)</label>
                    <input
                      type="number"
                      name="discount"
                      value={formData.discount}
                      onChange={handleInputChange}
                      min="0"
                      max="100"
                      step="any"
                      className="input-field bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Displayed Prices (charges incl. 2%)</label>
                    <div className="space-y-1">
                      <input
                        type="text"
                        value={formData.spaPrice ? `Spa: GHS ${(parseFloat(formData.spaPrice) * 1.02).toFixed(2)}` : ''}
                        className="input-field bg-gray-700 border-gray-600 text-gray-400 placeholder-gray-400 text-sm" readOnly
                      />
                      <input
                        type="text"
                        value={formData.mobilePrice ? `Mobile: GHS ${(parseFloat(formData.mobilePrice) * 1.02).toFixed(2)}` : ''}
                        className="input-field bg-gray-700 border-gray-600 text-gray-400 placeholder-gray-400 text-sm" readOnly
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Duration (minutes) <span className="text-red-500">*</span>
                  </label>
                  <p className="text-xs text-gray-400 mb-2">
                    Enter the actual length of this service (for example 30, 45, 60, 90).
                  </p>
                  <input
                    type="number"
                    name="duration"
                    value={formData.duration}
                    onChange={handleInputChange}
                    required
                    min="15"
                    className="input-field bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                    placeholder="e.g. 60"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Features & Benefits
                  </label>
                  <div className="space-y-2">
                    <div className="flex space-x-2">
                      <input
                        type="text"
                        value={newFeature}
                        onChange={(e) => setNewFeature(e.target.value)}
                        className="flex-1 input-field bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        placeholder="Add a feature or benefit"
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddFeature())}
                      />
                      <button
                        type="button"
                        onClick={handleAddFeature}
                        className="btn-secondary"
                      >
                        Add
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {formData.features.map((feature, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center space-x-1 bg-blue-500 bg-opacity-20 text-blue-300 px-3 py-1 rounded-full text-sm"
                        >
                          <span>{feature}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveFeature(index)}
                            className="text-blue-300 hover:text-blue-100"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {formData.category === 'Combo Packages' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Included services (shown on main site)
                    </label>
                    <p className="text-xs text-gray-400 mb-2">List the services that make up this combo (e.g. Swedish Massage, Hydrating Facial)</p>
                    <div className="space-y-2">
                      <div className="flex space-x-2">
                        <input
                          type="text"
                          value={newServiceName}
                          onChange={(e) => setNewServiceName(e.target.value)}
                          className="flex-1 input-field bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                          placeholder="e.g. Swedish Massage"
                          onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddServiceName())}
                        />
                        <button
                          type="button"
                          onClick={handleAddServiceName}
                          className="btn-secondary"
                        >
                          Add
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {(formData.serviceNames || []).map((name, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center space-x-1 bg-purple-500 bg-opacity-20 text-purple-300 px-3 py-1 rounded-full text-sm"
                          >
                            <span>{name}</span>
                            <button
                              type="button"
                              onClick={() => handleRemoveServiceName(index)}
                              className="text-purple-300 hover:text-purple-100"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-green-500 focus:ring-green-500 border-gray-600 rounded bg-gray-700"
                  />
                  <label className="ml-2 text-sm text-gray-300">
                    Service is active and available for booking
                  </label>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddModal(false)
                      setEditingService(null)
                    }}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    disabled={submitting}
                    className="btn-primary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? (
                      <>
                        <Loader className="w-4 h-4 animate-spin" />
                        <span>Saving...</span>
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        <span>{editingService ? 'Update Service' : 'Add Service'}</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}

export default Services
